// Route: /tests/results/$attemptId — экран результатов попытки.
// MSW endpoint: GET /api/quizzes/attempt/:attemptId
// См. docs/ui-spec/quizzes.results.[attemptId].{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { cn, ErrorState, NARROW_PAGE_SHELL, STATUS_PAGE_SHELL } from "@studyhub/ui";
import { Button } from "@studyhub/ui/components/button";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { IconArrowLeft, IconRefresh } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useAttemptResult } from "@/components/quizzes/_api";
import { ResultsView } from "@/components/quizzes/results/results-view";
import { OfflineDetailState } from "@/components/shared/offline-detail-state";
import { useWeakSpotsByAttempt } from "@/components/weak-spots/_api";
import { useRefetchOnOnline } from "@/hooks/use-refetch-on-online";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { fromSearchSchema, resolveBackFrom } from "@/lib/back-from";
import { isOfflineWithoutCache } from "@/lib/offline-query-state";
import { useAttemptRecordQuery } from "@/lib/powersync/queries/use-quiz-attempts-query";
import { api } from "@/lib/trpc-client";

const searchSchema = z
	.object({
		demo: z.enum(["loading", "error"]).optional(),
	})
	.merge(fromSearchSchema);

export const Route = createFileRoute("/tests/results/$attemptId")({
	validateSearch: tolerantSearch(searchSchema),
	component: ResultsPage,
});

function ResultsPage(): React.ReactElement {
	const { t } = useTranslation("quizzes");
	const { attemptId } = Route.useParams();
	const { from } = Route.useSearch();
	const backOrigin = resolveBackFrom(from);
	const query = useAttemptResult(attemptId);
	const weakSpotsQuery = useWeakSpotsByAttempt(attemptId);
	useRefetchOnOnline(() => query.refetch(), query.isError);
	// Попытка синкается в локальную SQLite, но экран читал только tRPC — офлайн
	// он показывал «Не удалось загрузить» поверх данных, которые лежат рядом.
	const localAttempt = useAttemptRecordQuery(attemptId).data?.[0];
	const isOffline = isOfflineWithoutCache(query);

	// Активность темы закрывает СЕРВЕР (Inngest `autoCompleteTopicActivity` на
	// `quiz/attempt.submitted`), но `useSubmitAttempt` инвалидирует только
	// ["quizzes"] — прогресс курса и плана остаётся старым. Сбрасываем кэш при
	// входе на экран результатов и повторно по терминальному socket-событию:
	// Inngest отрабатывает асинхронно и обычно уже ПОСЛЕ первого запроса.
	const utils = api.useUtils();
	useEffect(() => {
		void utils.courses.invalidate();
		void utils.studyPlan.invalidate();
	}, [utils]);
	useSocketEvent("course:topic_completed", () => {
		void utils.courses.invalidate();
		void utils.studyPlan.invalidate();
	});
	useSocketEvent("study-plan:progress_updated", () => {
		void utils.courses.invalidate();
		void utils.studyPlan.invalidate();
	});

	if (query.isLoading) {
		return (
			<div className={cn(NARROW_PAGE_SHELL, "space-y-7 py-9")}>
				<Skeleton className="h-48 w-full rounded-[var(--radius-panel)]" />
				<div className="space-y-2">
					<Skeleton className="h-16 w-full rounded-[var(--radius-card)]" />
					<Skeleton className="h-16 w-full rounded-[var(--radius-card)]" />
				</div>
				<Skeleton className="h-44 w-full rounded-[var(--radius-card)]" />
				<div className="space-y-2.5">
					<Skeleton className="h-28 w-full rounded-[var(--radius-card)]" />
					<Skeleton className="h-28 w-full rounded-[var(--radius-card)]" />
				</div>
			</div>
		);
	}

	if (isOffline) {
		return (
			<div className={cn(NARROW_PAGE_SHELL, "space-y-7 py-9")}>
				{localAttempt ? (
					<div className="rounded-[var(--radius-panel)] border border-border bg-card p-6 text-center shadow-e1">
						<p className="text-3xl font-semibold tabular-nums">{localAttempt.score}%</p>
						<p className="mt-1 text-sm text-muted-foreground">
							{localAttempt.correct_count} / {localAttempt.total_count}
						</p>
					</div>
				) : null}
				<OfflineDetailState onRetry={() => void query.refetch()} />
			</div>
		);
	}

	if (query.isError || !query.data) {
		return (
			<div className={STATUS_PAGE_SHELL}>
				<ErrorState
					title={t("list.errorTitle")}
					description={t("list.errorDescription")}
					action={
						<div className="flex flex-wrap items-center justify-center gap-2">
							<Button
								variant="outline"
								onClick={() => void query.refetch()}
								className="cursor-pointer max-sm:min-h-11"
							>
								<IconRefresh size={14} />
								{t("results.retry")}
							</Button>
							<Button asChild variant="ghost" className="max-sm:min-h-11">
								{backOrigin?.kind === "material" ? (
									<Link to="/materials/$materialId" params={{ materialId: backOrigin.materialId }}>
										<IconArrowLeft size={14} />
										{t("results.backToTest")}
									</Link>
								) : backOrigin?.kind === "subsection" ? (
									<Link
										to="/courses/$courseId/topics/$topicId"
										params={{
											courseId: backOrigin.planId,
											topicId: backOrigin.subsectionId,
										}}
									>
										<IconArrowLeft size={14} />
										{t("edit.backToList")}
									</Link>
								) : (
									<Link to="/tests">
										<IconArrowLeft size={14} />
										{t("edit.backToList")}
									</Link>
								)}
							</Button>
						</div>
					}
				/>
			</div>
		);
	}

	const { attempt, quiz, questions } = query.data;
	// Реальные weak-spots по attempt'у (Inngest analyzeWeakSpots — async после
	// submit). Пока workflow не отработал — fallback по breakdown accuracy<70.
	const detectedWeakSpots = weakSpotsQuery.data?.items.length ?? 0;
	const hasWeakSpots =
		detectedWeakSpots > 0 ||
		(weakSpotsQuery.isFetched &&
			Object.values(attempt.breakdownByTopic).some((b) => b.accuracy < 70));

	return (
		<>
			{backOrigin?.kind === "subsection" && (
				<div className={cn(NARROW_PAGE_SHELL, "pt-4")}>
					<Button asChild variant="ghost" size="sm" className="cursor-pointer max-sm:min-h-11">
						<Link
							to="/courses/$courseId/topics/$topicId"
							params={{ courseId: backOrigin.planId, topicId: backOrigin.subsectionId }}
						>
							<IconArrowLeft size={14} />
							{t("results.backToTopic")}
						</Link>
					</Button>
				</div>
			)}
			<ResultsView
				attempt={attempt}
				quiz={quiz}
				questions={questions}
				hasWeakSpots={hasWeakSpots}
			/>
		</>
	);
}
